import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, ViewChild } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AudioPlaylistPlayerService } from './services/audio-playlist-player.service';

@Component({
    selector: 'ndt-audio-playlist-player',
    templateUrl: './audio-playlist-player.component.html',
    styleUrls: ['./audio-playlist-player.component.scss'],
    standalone: false
})
export class AudioPlaylistPlayerComponent implements AfterViewInit, OnDestroy {
   @ViewChild('audioElement', { static: false }) public _audioRef: ElementRef;
   private audioPlayer: HTMLMediaElement;

   @Input() fileTokens: string[];

   @Input() autostart: boolean;

   src: string;

   currentPlaylistIndex: number;

   isPaused: boolean;

   isLoading: boolean;

   showError: boolean;

   autoPlayAll: boolean;

   playlistIndexSubscription: Subscription;

   isPausedSubscription: Subscription;

   isLoadingSubscription: Subscription;

   showErrorSubscription: Subscription;

   autoPlayAllSubscription: Subscription;

   private destroyed$ = new Subject<void>();

   constructor(public audioService: AudioPlaylistPlayerService, private cd: ChangeDetectorRef) {
      this.playlistIndexSubscription = this.audioService.currentPlaylistIndex
         .pipe(takeUntil(this.destroyed$))
         .subscribe((value) => {
            if (this.currentPlaylistIndex === value) return;
            this.currentPlaylistIndex = value;
            if (this.audioPlayer) {
               this.audioPlayer.onended = null;
               this.stopAudio();
               if (this.currentPlaylistIndex != -1) {
                  this.playAudio();
               } else {
                  this.src = '';
               }
            }
         });

      this.isPausedSubscription = this.audioService.isPaused.pipe(takeUntil(this.destroyed$)).subscribe((value) => {
         if (this.isPaused === value) return;
         this.isPaused = value;
         if (this.isPaused) {
            // check if user clicked play/pause button on parent component
            if (this.audioPlayer) this.pauseAudio();
         } else {
            if (this.audioPlayer) this.resumeAudio();
         }
      });

      this.isLoadingSubscription = this.audioService.isLoading.pipe(takeUntil(this.destroyed$)).subscribe((value) => {
         this.isLoading = value;
      });

      this.showErrorSubscription = this.audioService.showError.pipe(takeUntil(this.destroyed$)).subscribe((value) => {
         this.showError = value;
      });

      this.autoPlayAllSubscription = this.audioService.autoPlayAll
         .pipe(takeUntil(this.destroyed$))
         .subscribe((value) => {
            if (this.autoPlayAll === value) return;
            this.autoPlayAll = value;
            if (this.autoPlayAll) {
               if (this.audioPlayer && this.currentPlaylistIndex == 0 && !this.isPaused) {
                  this.audioPlayer.onended = null;
                  this.stopAudio();
                  this.playAudio();
               } else {
                  this.audioService.setPlaylistIndex(0);
               }
            } else {
               if (this.audioPlayer) {
                  this.stopAudio();
                  this.audioPlayer.onended = null;
               }
               this.audioService.setIsPaused(true);
               this.audioService.setPlaylistIndex(-1);
               this.src = '';
            }
         });
   }

   ngAfterViewInit(): void {
      this.audioPlayer = this._audioRef.nativeElement;

      // listen for user clicking play/pause button on the audio player
      this.audioPlayer.addEventListener('pause', () => {
         if ((this.audioPlayer.paused && this.audioPlayer.readyState == 0) || this.audioPlayer.currentTime > 0) {
            this.audioService.setIsPaused(true);
         }
      });
      this.audioPlayer.addEventListener('play', () => {
         this.audioService.setIsPaused(false);
      });
      this.audioPlayer.addEventListener('canplay', () => {
         this.audioService.setIsLoading(false);
      });

      if (this.autostart) {
         this.playAudio();
      } else {
         this.audioService.setIsPaused(true);
      }
   }

   playAudio() {
      const currentAudio = this.fileTokens[this.currentPlaylistIndex];
      this.src = currentAudio ?? '';
      this.cd.detectChanges();
      this.audioService.setIsLoading(true);
      this.audioPlayer.play().catch((error) => {
         if (error.code != 20) {
            // ignore error if user clicked play while audio is still loading
            console.error(error);
            this.audioService.setIsPaused(true);
            this.audioService.setIsLoading(false);
            this.audioService.setShowError(true);
         }
      });
      this.audioService.setShowError(false);
      if (this.autoPlayAll) {
         this.audioPlayer.onended = () => {
            this.playNext();
         };
      }
   }

   playNext() {
      if (this.currentPlaylistIndex < this.fileTokens.length - 1) {
         this.audioService.setPlaylistIndex(this.currentPlaylistIndex + 1);
         this.playAudio();
      } else if (this.currentPlaylistIndex === this.fileTokens.length - 1) {
         if (this.autoPlayAll) {
            this.audioService.setAutoPlayAll(false);
            this.audioService.setPlaylistIndex(-1);
         }
      }
   }

   pauseAudio() {
      this.audioPlayer.pause();
   }

   resumeAudio() {
      if (this.src) {
         this.audioPlayer.play().catch((error) => {
            if (error.code != 20) {
               this.audioService.setIsPaused(true);
               this.audioService.setShowError(true);
            }
         });
         this.audioService.setShowError(false);
      } else {
         this.playAudio();
      }
   }

   stopAudio() {
      this.audioPlayer.pause();
      this.audioPlayer.currentTime = 0;
   }

   onPreviousClick() {
      if (!this.fileTokens?.length) {
         this.audioService.setShowError(true);
         return;
      }
      if (this.audioPlayer.currentTime > 2.9) {
         this.audioPlayer.currentTime = 0;
         if (this.isPaused) this.audioPlayer.play();
      } else if (this.currentPlaylistIndex > 0) {
         this.audioService.setPlaylistIndex(this.currentPlaylistIndex - 1);
      } else {
         this.audioService.setPlaylistIndex(this.fileTokens.length - 1);
      }
   }

   onNextClick() {
      if (this.currentPlaylistIndex < this.fileTokens.length - 1) {
         this.audioService.setPlaylistIndex(this.currentPlaylistIndex + 1);
      } else {
         this.audioService.setPlaylistIndex(0);
      }
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
      // reset
      this.audioService.setPlaylistIndex(-1);
      this.audioService.setIsPaused(true);
      this.audioService.setIsLoading(false);
      this.audioService.setShowError(false);
      this.audioService.setAutoPlayAll(false);
   }
}
